'use client';

import { useTranslations } from 'next-intl';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  EllipsisVertical,
  ExternalLink,
  Eye,
  FileStack,
  FileText,
  History,
  Loader2,
  Pause,
  PauseCircle,
  Play,
  ScanText,
  Trash2,
  XCircle
} from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import { RouteGate } from '@/components/route-gate';
import { TemplateFieldsInfo } from '@/features/ocr-templates';
import type { OcrContainer } from '../types';
import { resolveContainerTemplate } from '../utils/container-template';
import { resolveOcrContainerLabel } from '../utils/container-label';
import { isInOcrQa, OcrQaStagePill } from './ocr-qa-stage-pill';
import { ContainerIdPrefix } from './container-id-prefix';

const OCR_STATUS_STYLES: Record<
  string,
  { className: string; icon: typeof CheckCircle }
> = {
  processing: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: Loader2
  },
  paused: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icon: PauseCircle
  },
  completed: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: CheckCircle
  },
  failed: {
    className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
    icon: XCircle
  }
};

interface ContainerCardProps {
  container: OcrContainer;
  isStarting: boolean;
  onStartOcr: (id: number) => void;
  onOpenResultsSummary: (container: OcrContainer) => void;
  onViewPdf: (container: OcrContainer) => void;
  onPauseOcr: (container: OcrContainer) => void;
  onResumeOcr: (container: OcrContainer) => void;
  onMoveToRepair: (container: OcrContainer) => void;
  onViewRepairHistory: (container: OcrContainer) => void;
  onDeleteTest: (container: OcrContainer) => void;
}

export function ContainerCard({
  container,
  isStarting,
  onStartOcr,
  onOpenResultsSummary,
  onViewPdf,
  onPauseOcr,
  onResumeOcr,
  onMoveToRepair,
  onViewRepairHistory,
  onDeleteTest
}: ContainerCardProps) {
  const t = useTranslations('ocr');
  const tRepair = useTranslations('repairs');

  const status = container.ocr_status;
  const stage = container.registration?.current_stage;
  const hasPdf = !!container.latest_batch?.pdf_path;
  const containerNumber = resolveOcrContainerLabel(
    {
      is_test: container.is_test,
      container_number: container.registration?.container_number,
      entity_name: container.entity_name
    },
    t('untitledTestDocument')
  );
  const { template: containerTemplate, extra: extraTemplateCount } =
    resolveContainerTemplate(container);
  const templateName = containerTemplate?.name ?? '-';
  const templateId = containerTemplate?.id ?? null;

  const statusStyle = status ? OCR_STATUS_STYLES[status] : null;
  const StatusIcon = statusStyle?.icon ?? Clock;
  // No `pending` arm: the backend folds pending into `processing` — the
  // distinction (queued vs. claimed by a worker) means nothing to an operator.
  const statusLabel =
    status === 'processing'
      ? t('statusProcessing')
      : status === 'paused'
        ? t('statusPaused')
        : status === 'completed'
          ? t('statusCompleted')
          : status === 'failed'
            ? t('statusFailed')
            : null;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-3'>
            <div className='bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
              <ScanText className='text-primary h-4 w-4' />
            </div>
            <div className='flex min-w-0 items-center gap-1.5'>
              {/* A container number — or an uploaded file name — carries the
                  document's own direction, never the shell's. */}
              <ContainerIdPrefix id={container.id} />
              <Link
                dir='auto'
                href={`/dashboard/ocr-containers/${container.id}`}
                className='hover:text-primary truncate font-medium hover:underline'
                title={containerNumber}
              >
                {containerNumber}
              </Link>
              {container.is_test && (
                <Badge
                  variant='outline'
                  className='shrink-0 border-purple-500/30 bg-purple-500/10 text-[10px] text-purple-600 dark:text-purple-400'
                >
                  {t('testBadge')}
                </Badge>
              )}
            </div>
          </div>
          {isInOcrQa(stage) ? (
            <span className='shrink-0'>
              <OcrQaStagePill stage={stage} />
            </span>
          ) : status && statusLabel ? (
            <Badge
              variant='outline'
              className={cn('shrink-0 gap-1 text-xs', statusStyle?.className)}
            >
              <StatusIcon
                className={cn(
                  'h-3 w-3',
                  status === 'processing' && 'animate-spin'
                )}
              />
              {statusLabel}
            </Badge>
          ) : (
            <span className='text-muted-foreground shrink-0 text-xs'>
              &mdash;
            </span>
          )}
        </div>

        <div className='mb-4 space-y-2 text-sm'>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('template')}</span>
            <span className='flex min-w-0 items-center gap-1.5 font-medium'>
              <span className='truncate'>{templateName}</span>
              {extraTemplateCount > 0 && (
                <span className='text-muted-foreground shrink-0'>
                  +{extraTemplateCount}
                </span>
              )}
              <TemplateFieldsInfo templateId={templateId} />
            </span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('pages')}</span>
            <span className='font-medium'>{container.total_pages ?? 0}</span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>{t('date')}</span>
            <span className='font-medium'>
              {new Date(container.created_at).toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: '2-digit'
              })}
            </span>
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          {!status ? (
            <PermissionGate permissions={['ocr.edit']}>
              <Button
                onClick={() => onStartOcr(container.id)}
                disabled={isStarting}
                variant='outline'
                size='sm'
                className='h-8'
              >
                {isStarting ? (
                  <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <ScanText className='me-1.5 h-3.5 w-3.5' />
                )}
                {t('startOcr')}
              </Button>
            </PermissionGate>
          ) : (
            <Button
              asChild
              variant={status === 'completed' ? 'default' : 'outline'}
              size='sm'
              className='h-8'
            >
              <Link href={`/dashboard/ocr-containers/${container.id}`}>
                {status === 'processing' ? (
                  <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <ExternalLink className='me-1.5 h-3.5 w-3.5' />
                )}
                {t('openResultsPage')}
              </Link>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='icon' className='h-8 w-8'>
                <EllipsisVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {/* Pause / resume — mutually exclusive by status. */}
              {status === 'processing' && (
                <PermissionGate permissions={['ocr.edit']}>
                  <DropdownMenuItem onClick={() => onPauseOcr(container)}>
                    <Pause className='me-2 h-3.5 w-3.5' />
                    {t('pauseOcr')}
                  </DropdownMenuItem>
                </PermissionGate>
              )}
              {status === 'paused' && (
                <PermissionGate permissions={['ocr.edit']}>
                  <DropdownMenuItem onClick={() => onResumeOcr(container)}>
                    <Play className='me-2 h-3.5 w-3.5' />
                    {t('resumeOcr')}
                  </DropdownMenuItem>
                </PermissionGate>
              )}

              {hasPdf && (
                <DropdownMenuItem onClick={() => onViewPdf(container)}>
                  <FileStack className='me-2 h-3.5 w-3.5' />
                  {t('viewPdf')}
                </DropdownMenuItem>
              )}

              {container.registration?.id && !isInOcrQa(stage) && (
                <PermissionGate permissions={['ocr.edit']}>
                  <DropdownMenuItem
                    onClick={() => onMoveToRepair(container)}
                    className='text-amber-600 focus:text-amber-600'
                  >
                    <AlertTriangle className='me-2 h-3.5 w-3.5' />
                    {tRepair('moveToRepair')}
                  </DropdownMenuItem>
                </PermissionGate>
              )}

              {container.repair_count > 0 && (
                <DropdownMenuItem
                  onClick={() => onViewRepairHistory(container)}
                >
                  <History className='me-2 h-3.5 w-3.5' />
                  {t('viewRepairHistory')}
                </DropdownMenuItem>
              )}

              {container.registration?.id && (
                <RouteGate
                  route={`/dashboard/containers/${container.registration.id}`}
                >
                  <DropdownMenuItem asChild>
                    <Link
                      href={`/dashboard/containers/${container.registration.id}`}
                    >
                      <Eye className='me-2 h-3.5 w-3.5' />
                      {t('viewContainer')}
                    </Link>
                  </DropdownMenuItem>
                </RouteGate>
              )}

              {/* "View results" — the page-by-page summary sheet, same as the
                  table view. The primary action beside it opens the standalone
                  results page. */}
              <DropdownMenuItem onClick={() => onOpenResultsSummary(container)}>
                <FileText className='me-2 h-3.5 w-3.5' />
                {t('viewResults')}
              </DropdownMenuItem>

              {container.is_test && (
                <PermissionGate permissions={['ocr.edit']}>
                  <DropdownMenuItem
                    onClick={() => onDeleteTest(container)}
                    className='text-destructive focus:text-destructive'
                  >
                    <Trash2 className='me-2 h-3.5 w-3.5' />
                    {t('deleteTestContainer')}
                  </DropdownMenuItem>
                </PermissionGate>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}
